"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase";
import { useI18n } from "@/i18n/I18nProvider";

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setAuthenticated(true);
      } else if (!pathname?.includes("/admin/login")) {
        router.replace("/admin/login/");
      }
      setLoading(false);
    });
  }, [pathname, router]);

  async function handleLogout() {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push("/admin/login/");
    router.refresh();
  }

  if (pathname?.includes("/admin/login")) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-32 animate-pulse rounded bg-slate-700" />
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8 flex flex-col gap-4 border-b border-slate-700 pb-4 md:flex-row md:items-center md:justify-between">
        <nav className="flex flex-wrap gap-2 md:gap-3 lg:gap-4">
          <Link
            href="/admin/dashboard/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/dashboard")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.dashboard")}
          </Link>
          <Link
            href="/admin/profile/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/profile")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.profile")}
          </Link>
          <Link
            href="/admin/projects/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/projects")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.projects")}
          </Link>
          <Link
            href="/admin/experiences/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/experiences")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.experiences")}
          </Link>
          <Link
            href="/admin/education/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/education")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.education")}
          </Link>
          <Link
            href="/admin/skills/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/skills")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.skills")}
          </Link>
          <Link
            href="/admin/certificates/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/certificates")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.certificates")}
          </Link>
          <Link
            href="/admin/music/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/music")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.music")}
          </Link>
          <Link
            href="/admin/blog/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/blog")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.blog")}
          </Link>
          <Link
            href="/admin/messages/"
            className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap border transition-colors ${
              pathname?.includes("/messages")
                ? "border-slate-500 bg-slate-800 text-white shadow-sm"
                : "border-transparent text-slate-400 hover:border-slate-600 hover:bg-slate-800/60 hover:text-white"
            }`}
          >
            {t("admin.messages")}
          </Link>
        </nav>
        <button
          onClick={handleLogout}
          className="self-start rounded-full px-3 py-1.5 text-sm text-slate-400 transition-colors hover:bg-slate-800/60 hover:text-white md:self-auto"
        >
          {t("admin.logout")}
        </button>
      </div>
      {children}
    </div>
  );
}
